import { useEffect, useMemo, useRef, useState } from "react";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { AlertTriangle, CalendarClock, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useCall } from "@/contexts/call-context";
import { queryClient } from "@/lib/queryClient";
import { ProjectPicker } from "@/components/calls/ProjectPicker";
import {
  type CalendarEventInput,
  type CalendarItem,
  useCalendarEvent,
  useCalendarOptions,
  useCancelCalendarEvent,
  useCreateCalendarEvent,
  useRespondCalendarEvent,
  useUpdateCalendarEvent,
  invalidateCalendar,
} from "@/hooks/use-calendar";

/** All calendar times are entered and displayed in German time. */
const DEFAULT_TIMEZONE = "Europe/Berlin";
/** Earliest allowed start for a new (or re-timed) scheduled call. */
const MIN_CALL_LEAD_MS = 3 * 60_000;
const DEFAULT_DURATION_MS = 60 * 60_000;
const fieldClass = "h-8 bg-gray-900 border-gray-600 text-gray-100 text-xs";
const selectClass = `${fieldClass} w-full rounded-md border px-2`;
// Native date/time inputs: make the picker icon white and pin it to the right edge.
const dateFieldClass = `${fieldClass} [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-90 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

function toLocalInput(date: Date) {
  return formatInTimeZone(date, DEFAULT_TIMEZONE, "yyyy-MM-dd'T'HH:mm");
}

/** Earliest selectable start (now + lead time), rounded up to the next minute. */
function minCallStart(now = Date.now()) {
  return toLocalInput(new Date(Math.ceil((now + MIN_CALL_LEAD_MS) / 60_000) * 60_000));
}

function plusDuration(localStart: string, ms = DEFAULT_DURATION_MS) {
  return toLocalInput(new Date(fromZonedTime(localStart, DEFAULT_TIMEZONE).getTime() + ms));
}

interface CalendarEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: CalendarItem | null;
  initialKind?: "event" | "call";
  onSaved?: () => void;
}

function datetimeValue(value: string | undefined, timezone: string) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T09:00`;
  return formatInTimeZone(new Date(value), timezone, "yyyy-MM-dd'T'HH:mm");
}

function initialTimes() {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  // Next full hour, but never closer than the call lead time (e.g. at 14:58 → 16:00).
  if (start.getTime() < Date.now() + MIN_CALL_LEAD_MS) start.setHours(start.getHours() + 1);
  const end = new Date(start.getTime() + 60 * 60_000);
  return { start: toLocalInput(start), end: toLocalInput(end) };
}

function errorStatus(error: unknown) {
  return typeof error === "object" && error && "status" in error
    ? Number((error as { status: unknown }).status)
    : null;
}

function openPulseLink(pulseId: number, projectId: string | null) {
  if (!projectId) {
    window.dispatchEvent(new CustomEvent("open-pulse-workbench", { detail: { pulseId } }));
    return;
  }
  const params = new URLSearchParams({
    calendarProject: projectId,
    calendarPulse: String(pulseId),
  });
  window.history.pushState(window.history.state, "", `/?${params.toString()}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function CalendarEventDialog({
  open,
  onOpenChange,
  item,
  initialKind = "event",
  onSaved,
}: CalendarEventDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const call = useCall() as ReturnType<typeof useCall> & {
    startCalendarCall?: (eventId: string) => Promise<void>;
  };
  const isStoredEvent = item?.sourceType === "event";
  const details = useCalendarEvent(isStoredEvent ? item.id : null, open && !!isStoredEvent);
  const current = details.data ?? item ?? null;
  const readOnlyView = item?.readOnly === true;
  const options = useCalendarOptions(open);
  const createEvent = useCreateCalendarEvent();
  const updateEvent = useUpdateCalendarEvent();
  const cancelEvent = useCancelCalendarEvent();
  const respond = useRespondCalendarEvent();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [responding, setResponding] = useState(false);
  const [starting, setStarting] = useState(false);
  const respondPendingRef = useRef(false);
  const startPendingRef = useRef(false);
  const [editing, setEditing] = useState(!item);
  const defaults = useMemo(initialTimes, [open]);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<"event" | "call">(initialKind);
  const [callType, setCallType] = useState<"audio" | "video">("video");
  const [allDay, setAllDay] = useState(false);
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const timezone = DEFAULT_TIMEZONE;
  const [scope, setScope] = useState<"personal" | "company">("personal");
  const [ownerUserId, setOwnerUserId] = useState(user?.id ?? 0);
  const [projectId, setProjectId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [attendeeUserIds, setAttendeeUserIds] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;
    const source = current;
    // Entries saved in another zone are still shown/edited in German time.
    const zone = DEFAULT_TIMEZONE;
    setEditing(!source);
    setTitle(source?.title ?? "");
    setKind(source?.kind === "call" ? "call" : initialKind);
    setCallType(source?.callType === "audio" ? "audio" : "video");
    setAllDay(source?.kind === "call" ? false : (source?.allDay ?? false));
    setStart(source ? datetimeValue(source.startAt, zone) : defaults.start);
    setEnd(source ? datetimeValue(source.endAt, zone) : defaults.end);
    setScope(source?.scope ?? "personal");
    setOwnerUserId(source?.ownerUserId ?? user?.id ?? 0);
    setProjectId(source?.projectId ?? "");
    setBusy(source?.busy ?? false);
    setAttendeeUserIds(source?.attendees?.map((attendee) => attendee.userId) ?? []);
  }, [open, current?.id, current?.version, current?.status, initialKind, defaults.start, defaults.end, user?.id]);

  useEffect(() => {
    if (kind === "call" && callType === "video") setBusy(true);
  }, [kind, callType]);

  const selectedProject = options.data?.projects.find((project) => project.id === projectId);
  const pending = createEvent.isPending || updateEvent.isPending || cancelEvent.isPending;
  // Agenda rows can be stale by the time a dialog opens. Never offer a
  // mutation until the stored event detail endpoint has supplied its current
  // status and permissions. In particular, a failed first detail request must
  // not leave controls from the agenda snapshot available.
  const hasFreshStoredDetails = isStoredEvent
    && !!details.data
    && !details.isLoading
    && !details.isFetching
    && !details.isError;
  const isScheduled = current?.status?.toLowerCase() === "scheduled";
  const canMutateCurrent = hasFreshStoredDetails && isScheduled;
  const canEdit = !readOnlyView && canMutateCurrent && current?.canEdit === true;
  const canCancel = !readOnlyView && canMutateCurrent && current?.canCancel === true;
  const isProjected = !!current && current.sourceType !== "event";
  const canRespond = !readOnlyView && canMutateCurrent && current?.response != null;
  const canStartCall = canMutateCurrent
    && !readOnlyView
    && current?.kind === "call"
    && (current.callType === "audio" || current.callType === "video")
    && (
      user?.role === "admin"
      || current.ownerUserId === user?.id
      || current.organizerUserId === user?.id
      || current.attendees?.some((attendee) => attendee.userId === user?.id && attendee.response !== "declined") === true
    );

  const toPayload = (): CalendarEventInput | null => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast({ title: "Title required", description: "Enter a title for this calendar entry.", variant: "destructive" });
      return null;
    }
    if (!start || !end) {
      toast({ title: "Dates required", description: "Choose a start and end.", variant: "destructive" });
      return null;
    }
    if (kind === "call") {
      const participants = new Set([ownerUserId, user?.id, ...attendeeUserIds].filter((id): id is number => typeof id === "number" && id > 0));
      if (participants.size < 2) {
        toast({ title: "Call recipient required", description: "A scheduled call needs at least two participants.", variant: "destructive" });
        return null;
      }
      if (participants.size > 4) {
        toast({ title: "Too many call participants", description: "Scheduled calls support at most four participants.", variant: "destructive" });
        return null;
      }
    }
    let startAt: string;
    let endAt: string;
    if (allDay) {
      startAt = start.slice(0, 10);
      endAt = end.slice(0, 10);
      if (endAt <= startAt) {
        toast({ title: "Invalid date range", description: "For all-day entries, the exclusive end date must follow the start date.", variant: "destructive" });
        return null;
      }
    } else {
      const startDate = fromZonedTime(start, timezone);
      const endDate = fromZonedTime(end, timezone);
      // Calls can't be scheduled in the past; an existing call keeps its
      // original start untouched, only a changed start is checked.
      const originalStart = current ? datetimeValue(current.startAt, timezone) : null;
      if (kind === "call" && start !== originalStart && startDate.getTime() < Date.now() + MIN_CALL_LEAD_MS) {
        toast({ title: "Start is too soon", description: "A scheduled call must start at least 3 minutes from now.", variant: "destructive" });
        return null;
      }
      if (endDate <= startDate) {
        toast({ title: "Invalid time range", description: "The end must be after the start.", variant: "destructive" });
        return null;
      }
      startAt = startDate.toISOString();
      endAt = endDate.toISOString();
    }
    return {
      title: trimmedTitle,
      startAt,
      endAt,
      allDay,
      timezone,
      scope,
      ownerUserId,
      projectId: projectId || null,
      kind,
      callType,
      busy: kind === "call" && callType === "video" ? true : busy,
      attendeeUserIds,
    };
  };

  const handleSave = async () => {
    if (current && !canEdit) return;
    const input = toPayload();
    if (!input) return;
    try {
      if (current) {
        if (current.sourceType !== "event" || current.version == null) return;
        await updateEvent.mutateAsync({ id: current.id, version: current.version, input });
      } else {
        await createEvent.mutateAsync(input);
      }
      toast({ title: current ? "Calendar entry updated" : "Calendar entry created" });
      onOpenChange(false);
      onSaved?.();
    } catch (error: any) {
      if (errorStatus(error) === 409 || errorStatus(error) === 412) {
        await details.refetch();
        toast({
          title: "This entry changed",
          description: "A newer version was loaded. Review it before saving again.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Could not save entry", description: error?.message, variant: "destructive" });
    }
  };

  const handleCancel = async () => {
    if (!current || !canCancel || current.version == null) return;
    try {
      await cancelEvent.mutateAsync({ id: current.id, version: current.version });
      setConfirmCancel(false);
      onOpenChange(false);
      onSaved?.();
      toast({ title: "Calendar entry cancelled" });
    } catch (error: any) {
      setConfirmCancel(false);
      if (errorStatus(error) === 409 || errorStatus(error) === 412) {
        await details.refetch();
        toast({ title: "This entry changed", description: "The latest version has been loaded; it was not cancelled.", variant: "destructive" });
      } else {
        toast({ title: "Could not cancel entry", description: error?.message, variant: "destructive" });
      }
    }
  };

  const handleRespond = async (response: "accepted" | "declined") => {
    if (!current || !canRespond || respondPendingRef.current) return;
    respondPendingRef.current = true;
    setResponding(true);
    try {
      await respond.mutateAsync({ id: current.id, response });
      await details.refetch().catch(() => undefined);
      toast({ title: response === "accepted" ? "Invitation accepted" : "Invitation declined" });
      onSaved?.();
    } catch (error: any) {
      if (errorStatus(error) === 409) {
        await Promise.all([
          details.refetch(),
          invalidateCalendar(queryClient),
        ]).catch(() => undefined);
        toast({
          title: "This entry changed",
          description: "This calendar event is no longer scheduled. The latest status is shown.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Could not update response", description: error?.message, variant: "destructive" });
      }
    } finally {
      respondPendingRef.current = false;
      setResponding(false);
    }
  };

  const handleStart = async () => {
    if (!current || !canStartCall || !call.startCalendarCall || startPendingRef.current) return;
    startPendingRef.current = true;
    setStarting(true);
    try {
      await call.startCalendarCall(current.id);
      await Promise.all([
        invalidateCalendar(queryClient),
        details.refetch(),
      ]).catch(() => undefined);
    } catch (error: any) {
      if (errorStatus(error) === 409) {
        await Promise.all([
          details.refetch(),
          invalidateCalendar(queryClient),
        ]).catch(() => undefined);
        toast({
          title: "This call changed",
          description: "This calendar call is no longer scheduled. The latest status is shown.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Could not start appointment", description: error?.message, variant: "destructive" });
      }
    } finally {
      startPendingRef.current = false;
      setStarting(false);
    }
  };

  const openActiveCalls = () => {
    window.dispatchEvent(new CustomEvent("open-active-calls"));
    onOpenChange(false);
  };

  const toggleAttendee = (id: number, checked: boolean) => {
    setAttendeeUserIds((previous) =>
      checked ? [...new Set([...previous, id])] : previous.filter((value) => value !== id),
    );
  };

  const isCall = kind === "call";
  const startMin = isCall ? minCallStart() : undefined;
  const [startClamped, setStartClamped] = useState(false);
  const handleStartChange = (raw: string) => {
    // Browsers don't enforce `min` on datetime-local pickers (the date can be
    // typed or picked freely), so snap anything too early to the earliest slot.
    const floor = isCall ? minCallStart() : undefined;
    const clamped = !!(raw && floor && raw < floor);
    const value = clamped ? floor! : raw;
    setStartClamped(clamped);
    setStart(value);
    // Keep a sensible default: the end follows the start by one hour.
    if (value) setEnd(plusDuration(value));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-xl max-h-[92vh] overflow-y-auto bg-gray-800 border-gray-600 text-gray-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4" />
              {current ? current.title : kind === "call" ? "Schedule call" : "New event"}
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              {isProjected
                ? "This date comes from its original workflow and is read-only here."
                : "Times are in German time (Europe/Berlin). Changes use version-safe saving."}
            </DialogDescription>
          </DialogHeader>

          {details.isLoading && isStoredEvent ? (
            <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : details.isError && isStoredEvent ? (
            <div role="alert" className="rounded border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200">
              This calendar entry could not be loaded. It may have been removed or you may no longer have access.
            </div>
          ) : editing && !isProjected ? (
            <div className="space-y-3" data-testid="calendar-event-form">
              {options.isError && (
                <div role="alert" className="rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
                  Active users and projects could not be loaded. Try again before saving.
                </div>
              )}
              {/* The entry type is fixed by how the dialog was opened (Event vs. Schedule call). */}
              <div className="space-y-1">
                <Label htmlFor="calendar-title" className="text-xs">{isCall ? "Title / Topic" : "Title"}</Label>
                <Input id="calendar-title" value={title} onChange={(event) => setTitle(event.target.value)} className={fieldClass} autoFocus={!current} data-testid="calendar-title" />
              </div>
              {isCall && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="calendar-video"
                    checked={callType === "video"}
                    onCheckedChange={(value) => setCallType(value === true ? "video" : "audio")}
                    data-testid="calendar-video-toggle"
                  />
                  <Label htmlFor="calendar-video" className="text-xs">
                    Video <span className="text-gray-400">(off = audio call)</span>
                  </Label>
                </div>
              )}
              {!isCall && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="calendar-all-day"
                  checked={allDay}
                  onCheckedChange={(value) => {
                    const checked = value === true;
                    setAllDay(checked);
                    if (checked && end.slice(0, 10) <= start.slice(0, 10)) {
                      const next = new Date(`${start.slice(0, 10)}T12:00:00Z`);
                      next.setUTCDate(next.getUTCDate() + 1);
                      setEnd(`${next.toISOString().slice(0, 10)}T17:00`);
                    }
                  }}
                />
                <Label htmlFor="calendar-all-day" className="text-xs">All day</Label>
              </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="calendar-start" className="text-xs">Starts</Label>
                  <Input id="calendar-start" type={allDay ? "date" : "datetime-local"} min={allDay ? startMin?.slice(0, 10) : startMin} value={allDay ? start.slice(0, 10) : start} onChange={(event) => handleStartChange(allDay ? `${event.target.value}T09:00` : event.target.value)} className={dateFieldClass} data-testid="calendar-start" />
                  {startClamped && <p className="text-[10px] text-amber-300" data-testid="calendar-start-clamped">Moved to the earliest possible start (3 minutes from now).</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="calendar-end" className="text-xs">Ends{allDay ? " (exclusive)" : ""}</Label>
                  <Input id="calendar-end" type={allDay ? "date" : "datetime-local"} min={allDay ? start.slice(0, 10) : start} value={allDay ? end.slice(0, 10) : end} onChange={(event) => setEnd(allDay ? `${event.target.value}T17:00` : event.target.value)} className={dateFieldClass} data-testid="calendar-end" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="calendar-scope" className="text-xs">Calendar</Label>
                  <select id="calendar-scope" value={scope} onChange={(event) => setScope(event.target.value as "personal" | "company")} className={selectClass}>
                    <option value="personal">Personal</option>
                    <option value="company">Company</option>
                  </select>
                </div>
                {options.data?.isAdmin && (
                  <div className="space-y-1">
                    <Label htmlFor="calendar-owner" className="text-xs">Owner</Label>
                    <select id="calendar-owner" value={ownerUserId} onChange={(event) => setOwnerUserId(Number(event.target.value))} className={selectClass}>
                      {options.data.users.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="calendar-project" className="text-xs">Project (optional)</Label>
                <ProjectPicker
                  id="calendar-project"
                  testId="calendar-project"
                  value={projectId}
                  onChange={setProjectId}
                  selected={selectedProject ? { id: selectedProject.id, projectName: selectedProject.name, thumbnail: selectedProject.thumbnail } : null}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="calendar-busy"
                  checked={busy}
                  disabled={kind === "call" && callType === "video"}
                  onCheckedChange={(value) => setBusy(value === true)}
                />
                <Label htmlFor="calendar-busy" className="text-xs">
                  Busy {kind === "call" && callType === "video" && "(required for video)"}
                </Label>
              </div>
              <fieldset className="border border-gray-700 rounded p-2">
                <legend className="px-1 text-xs text-gray-300">Recipients / attendees</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-28 overflow-y-auto">
                  {options.data?.users.map((option) => (
                    <label key={option.id} className="flex items-center gap-2 rounded px-1 py-1 hover:bg-gray-700">
                      <Checkbox
                        checked={attendeeUserIds.includes(option.id)}
                        onCheckedChange={(value) => toggleAttendee(option.id, value === true)}
                      />
                      <span className="text-xs truncate">{option.name}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          ) : current ? (
            <div className="space-y-3 text-xs" data-testid="calendar-event-details">
              <div className="grid grid-cols-[6rem_1fr] gap-2">
                <span className="text-gray-400">When</span>
                <span>{current.allDay ? `${current.startAt} – ${current.endAt}` : `${new Date(current.startAt).toLocaleString()} – ${new Date(current.endAt).toLocaleString()}`}</span>
                <span className="text-gray-400">Project</span>
                <span>{current.projectName ? `${current.projectId} · ${current.projectName}` : "None"}</span>
                <span className="text-gray-400">Availability</span>
                <span>{current.busy ? "Busy" : "Available"}</span>
                {current.sourceType === "event" && current.status && (
                  <>
                    <span className="text-gray-400">Status</span>
                    <span className="capitalize">{current.status}</span>
                  </>
                )}
                {current.attendees && (
                  <>
                    <span className="text-gray-400">Attendees</span>
                    <span>{current.attendees.map((attendee) => `${attendee.name ?? attendee.userId}${attendee.response ? ` (${attendee.response})` : ""}`).join(", ") || "None"}</span>
                  </>
                )}
              </div>
              {current.kind === "call" && current.status === "started" && (
                <div className="rounded border border-sky-500/40 bg-sky-500/10 p-2 text-sky-100">
                  This call has already been started and is read-only. If it is still running, you can join from Active calls.
                </div>
              )}
              {current.kind === "call" && current.status === "starting" && (
                <div className="rounded border border-sky-500/40 bg-sky-500/10 p-2 text-sky-100">
                  This call is being started and is read-only. Check Active calls to join it.
                </div>
              )}
              {current.kind === "call" && current.status === "cancelled" && (
                <div className="rounded border border-gray-600 bg-gray-900/40 p-2 text-gray-300">
                  This calendar call was cancelled and is read-only.
                </div>
              )}
              {current.pulseLinks.length > 0 && (
                <div className="border-t border-gray-700 pt-2">
                  <div className="text-gray-400 mb-1">Related call notes</div>
                  <div className="flex flex-wrap gap-1">
                    {current.pulseLinks.map((link) => (
                      <Button
                        key={link.pulseId}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-gray-600"
                        onClick={() => openPulseLink(link.pulseId, link.projectId)}
                      >
                        {link.title || `Pulse ${link.pulseId}`}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="gap-1 sm:gap-1">
            {current && !editing && current.sourceType === "event" && (
              <>
                {canRespond && (
                  <>
                    <Button type="button" size="sm" variant="outline" className="h-8 border-gray-600" onClick={() => handleRespond("accepted")} disabled={respond.isPending || responding}>Accept</Button>
                    <Button type="button" size="sm" variant="outline" className="h-8 border-gray-600" onClick={() => handleRespond("declined")} disabled={respond.isPending || responding}>Decline</Button>
                  </>
                )}
                {canStartCall && (
                  <Button type="button" size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-500" onClick={handleStart} disabled={!call.startCalendarCall || starting}>Start call</Button>
                )}
                {canCancel && <Button type="button" size="sm" variant="ghost" className="h-8 text-red-300" onClick={() => setConfirmCancel(true)}><Trash2 className="h-3 w-3 mr-1" />Cancel</Button>}
                {canEdit && <Button type="button" size="sm" className="h-8" onClick={() => setEditing(true)}>Edit</Button>}
                {current.kind === "call" && (current.status === "started" || current.status === "starting") && (
                  <Button type="button" size="sm" variant="outline" className="h-8 border-sky-500/60 text-sky-200" onClick={openActiveCalls}>
                    View active calls
                  </Button>
                )}
              </>
            )}
            {editing && !isProjected && (
              <>
                {current && <Button type="button" size="sm" variant="outline" className="h-8 border-gray-600" onClick={() => setEditing(false)}>Back</Button>}
                <Button type="button" size="sm" className="h-8" onClick={handleSave} disabled={pending || options.isLoading || options.isError || (!!current && !canEdit)}>
                  {pending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Save
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent className="bg-gray-800 border-gray-600 text-gray-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" />Cancel this entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Attendees will no longer see it. This action uses the version you opened and will stop if someone changed it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-600">Keep</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-500" onClick={handleCancel} disabled={!canCancel || cancelEvent.isPending}>Cancel entry</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}